import { apiClient } from '@/services/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EmployeeOfferDashboard {
  activeOffers: number;
  pendingSync: number;
  contractorReview: number;
  notFound: number;
  deactivatedThisMonth: number;
}

export interface EmployeeOfferRecord {
  employeeOfferId: number;
  employeeId: string;
  employeeEmail: string;
  employeeFirstName: string;
  employeeLastName: string;
  employeeStartDate: string;
  employeeStatus: string;
  employeeStatusLastChecked: string;
  businessAgreement: string;
  createUser: string;
  lastUpdateUser: string;
  createDt: string;
  lastUpdateDt: string;
  effStartDt: string;
  effEndDt?: string;
}

export interface EmployeeOfferSynchRecord {
  employeeOfferSynchId: number;
  employeeEmail: string;
  employeeFirstName: string;
  employeeLastName: string;
  businessAgreement?: string;
  imported?: number;
  importedDt?: string;
  reviewStatus?: string;
  positionTitle?: string;
  employeeIdMatched?: string;
  employeeEmailMatched?: string;
  reviewedBy?: string;
  reviewedDt?: string;
  createDt: string;
  lastUpdateDt: string;
  effStartDt: string;
  effEndDt?: string;
}

export interface ImportLog {
  importLogId: number;
  description: string;
  createDt: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

// ─── API functions ────────────────────────────────────────────────────────────

export const getDashboard = async (): Promise<EmployeeOfferDashboard> => {
  const res = await apiClient.get<EmployeeOfferDashboard>('/api/employeeoffer/dashboard');
  return res.data;
};

export const getOffers = async (
  page: number,
  pageSize: number,
  search?: string,
  includeInactive?: boolean,
  deactivatedOnly?: boolean
): Promise<PagedResult<EmployeeOfferRecord>> => {
  const res = await apiClient.get<PagedResult<EmployeeOfferRecord>>('/api/employeeoffer/offers', {
    params: {
      page, pageSize,
      search: search || undefined,
      includeInactive: includeInactive || false,
      deactivatedOnly: deactivatedOnly || false,
    },
  });
  return res.data;
};

export const updateBusinessAgreement = async (id: number, businessAgreement: string): Promise<void> => {
  await apiClient.patch(`/api/employeeoffer/offers/${id}/business-agreement`, { businessAgreement });
};

export const deactivateOffer = async (id: number): Promise<void> => {
  await apiClient.post(`/api/employeeoffer/offers/${id}/deactivate`);
};

export const reactivateOffer = async (id: number): Promise<void> => {
  await apiClient.post(`/api/employeeoffer/offers/${id}/reactivate`);
};

export const getSynchQueue = async (
  page: number,
  pageSize: number,
  reviewStatus?: string
): Promise<PagedResult<EmployeeOfferSynchRecord>> => {
  const res = await apiClient.get<PagedResult<EmployeeOfferSynchRecord>>('/api/employeeoffer/synch', {
    params: { page, pageSize, reviewStatus: reviewStatus || undefined },
  });
  return res.data;
};

export const approveSynchRecord = async (id: number): Promise<void> => {
  await apiClient.post(`/api/employeeoffer/synch/${id}/approve`);
};

export const dismissSynchRecord = async (id: number): Promise<void> => {
  await apiClient.post(`/api/employeeoffer/synch/${id}/dismiss`);
};

export const getImportLogs = async (
  page: number,
  pageSize: number
): Promise<PagedResult<ImportLog>> => {
  const res = await apiClient.get<PagedResult<ImportLog>>('/api/employeeoffer/logs', {
    params: { page, pageSize },
  });
  return res.data;
};

export interface SyncReportRun {
  m365ReportRunId: number;
  reportStatus: string;
  reportResult?: string;
  reportStartDate?: string;
  reportEndDate?: string;
  createDt: string;
  reportLink?: string;
  reportFilename?: string;
}

export const getReportRuns = async (
  page: number,
  pageSize: number
): Promise<PagedResult<SyncReportRun>> => {
  const res = await apiClient.get<PagedResult<SyncReportRun>>('/api/employeeoffer/report-runs', {
    params: { page, pageSize },
  });
  return res.data;
};
